import { useState } from "preact/compat"
import { format, parseISO } from "date-fns"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast"
import { upsertIntegration } from "@/api/integration"
import { PasswordViewCopyButtons } from "./password-view-copy-buttons"

interface UpdateCredentialsFormProps {
  integrationName: string
  integrationId: string
  initialUsername: string
  credentialUpdatedAt?: string
  passwordRequired?: boolean
  className?: string
  onSuccess?: () => void
}

export function UpdateCredentialsForm({
  integrationName,
  integrationId,
  initialUsername,
  credentialUpdatedAt,
  passwordRequired = false,
  className,
  onSuccess,
}: UpdateCredentialsFormProps) {
  const [loading, setLoading] = useState(false)

  const form = useFormState({
    username: initialUsername,
    password: "",
  })

  async function onSubmit() {
    setLoading(true)
    form.set_form_errors([])

    const username = form.username.value.trim()
    const password = form.password.value

    if (!username) {
      form.set_form_errors(["Username is required"])
      setLoading(false)
      return
    }

    if (passwordRequired && !password) {
      form.set_form_errors(["Password is required"])
      setLoading(false)
      return
    }

    if (password) {
      const res = await upsertIntegration(integrationName, username, { password })
      setLoading(false)
      if (!res.ok) {
        form.set_form_errors([res.error.message || "Failed to update credentials. Please try again."])
        return
      }
    } else if (username !== initialUsername) {
      setLoading(false)
      form.set_form_errors(["Enter your password to confirm the username change."])
      return
    } else {
      setLoading(false)
      form.set_form_errors(["Enter a new password or change the username to save."])
      return
    }

    toast({
      title: "Credentials updated",
      description: "Your connection credentials have been saved.",
    })
    onSuccess?.()
  }

  let lastUpdatedLabel: string | null = null
  if (credentialUpdatedAt) {
    try {
      lastUpdatedLabel = format(parseISO(credentialUpdatedAt), "MMM d, yyyy")
    } catch {
      lastUpdatedLabel = null
    }
  }

  return (
    <div class={className}>
      <Form class="space-y-4" onSubmit={onSubmit} alerts={form.form_alerts}>
        <Input
          label="Username"
          type="text"
          name="username"
          required={true}
          bind={form.username}
          noAutofill
        />
        <PasswordInput
          label="Password"
          required={passwordRequired}
          bind={form.password}
          placeholder="Enter new password to change"
          noAutofill
        />
        <PasswordViewCopyButtons integrationId={integrationId} />
        {lastUpdatedLabel && (
          <p class="text-xs text-gray-500">Last updated {lastUpdatedLabel}</p>
        )}
        <div class="flex justify-end">
          <Button type="submit" variant="outline" disabled={loading}>
            {loading ? "Saving..." : "Save Credentials"}
          </Button>
        </div>
      </Form>
    </div>
  )
}
